import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LogOut, User as UserIcon, Building2, IdCard, Clock, Bell, Compass, ShieldCheck, Database, KeyRound } from "lucide-react";
import { useAuth } from "@/store/auth";
import { useIsCEO } from "@/hooks/useIsCEO";
import { useAccessRequestsBadge } from "@/hooks/useAccessRequestsBadge";
import { runTour, restartTourResetLocal } from "@/lib/tours/tourEngine";
import api from "@/lib/api";

const initials = (name = "") =>
  name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";

export function ProfileMenu() {
  const { t } = useTranslation();
  const { user, branch, logout } = useAuth();
  const isCEO = useIsCEO();
  const navigate = useNavigate();
  const { mineOpen: myAccessRequestsOpen } = useAccessRequestsBadge();   // D55d-uat3

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const onRestartTour = async () => {
    // Clear the two localStorage flags first so a page reload wouldn't
    // suppress the tour. Then reset the server flag for full parity.
    restartTourResetLocal();
    try { await api.post("/me/tour/reset"); } catch { /* best effort */ }
    // Fire the tour immediately for the current user's role.
    runTour(String(user?.role || "SALES").toUpperCase(), {
      navigate, user,
    });
  };

  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="profile-menu-trigger"
          className="inline-flex items-center gap-2 h-10 pl-1 pr-3 rounded-xl bg-white/5 border border-[var(--surface-border)] hover:bg-white/10"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-gradient-primary text-white text-xs font-bold">
              {initials(user.name)}
            </AvatarFallback>
          </Avatar>
          <div className="hidden sm:block text-left leading-tight">
            <div className="text-xs font-medium">{user.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{user.designation || "—"}</div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[260px] bg-popover/95 backdrop-blur-xl border-[var(--surface-border)]"
        data-testid="profile-menu-content"
      >
        <DropdownMenuLabel>
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-gradient-primary text-white text-sm font-bold">
                {initials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-sm font-semibold">{user.name}</div>
              <div className="text-[11px] text-muted-foreground">{user.email}</div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-my-profile">
          <IdCard className="mr-2 h-4 w-4" />
          <span>My Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/me/timesheet")} data-testid="profile-menu-timesheet">
          <Clock className="mr-2 h-4 w-4" />
          <span>{t("timesheet.title")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/me/onboarding")} data-testid="profile-menu-onboarding">
          <IdCard className="mr-2 h-4 w-4" />
          <span>{t("onb.myTitle")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings/notifications")} data-testid="profile-menu-notifications">
          <Bell className="mr-2 h-4 w-4" />
          <span>{t("notif.menuLink")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/access-requests?tab=mine")} data-testid="profile-menu-my-access-requests">
          <KeyRound className="mr-2 h-4 w-4" />
          <span className="flex-1">My Access Requests</span>
          {myAccessRequestsOpen > 0 && (
            <span
              data-testid="profile-menu-my-access-requests-badge"
              className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-rose-500 text-white"
            >
              {myAccessRequestsOpen > 9 ? "9+" : myAccessRequestsOpen}
            </span>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onRestartTour} data-testid="profile-menu-restart-tour">
          <Compass className="mr-2 h-4 w-4" />
          <span>{t("tour.restart", "Restart tour")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => window.dispatchEvent(new CustomEvent("open-compliance-banner"))}
          data-testid="profile-menu-chat-policy"
        >
          <ShieldCheck className="mr-2 h-4 w-4" />
          <span>{t("compliance.chatPolicyMenu", "Company chat policy")}</span>
        </DropdownMenuItem>
        {isCEO && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-foreground/55 py-1">
              CEO tools
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigate("/admin/data-ops/cleanup")}
              data-testid="profile-menu-data-cleanup"
            >
              <Database className="mr-2 h-4 w-4" />
              <span>Data Cleanup</span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem disabled>
          <UserIcon className="mr-2 h-4 w-4" />
          <span className="text-xs">{user.designation || "—"}</span>
        </DropdownMenuItem>
        {branch && (
          <DropdownMenuItem disabled data-testid="profile-branch">
            <Building2 className="mr-2 h-4 w-4" />
            <span className="text-xs">{branch.name} · {branch.code}</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} data-testid="logout-btn">
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t("common.logout")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ProfileMenu;
